"use client";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ProductCard } from "./ProductCard";
import { InfiniteScrollTrigger } from "./InfiniteScrollTrigger";
import type { PublicCommunityProduct } from "@/lib/community/types";

interface ShopProductGridProps {
  products: PublicCommunityProduct[];
  total: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  shopSlug?: string | null;
  error: Error | null;
  onRetry: () => void;
  onClearSearch: () => void;
  returnTo?: string;
}

export function ShopProductGrid({
  products,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  shopSlug,
  error,
  onRetry,
  onClearSearch,
  returnTo,
}: ShopProductGridProps) {
  if (!isLoading && products.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-muted">
          {error ? "Could not load products." : "No products found."}
        </p>
        <Button variant="secondary" onClick={error ? onRetry : onClearSearch} className="mt-4">
          {error ? "Try again" : "Clear product search"}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} shopSlug={shopSlug} returnTo={returnTo} />
        ))}
        {(isLoading || isFetchingNextPage) &&
          Array.from({ length: 8 }).map((_, i) => (
            <Card key={`skeleton-${i}`} className="h-64 animate-pulse" />
          ))}
      </div>

      {error && (
        <p role="status" className="mt-6 text-center text-muted">
          Could not load more products. Your results are still here.
        </p>
      )}
      <InfiniteScrollTrigger
        hasNextPage={hasNextPage || !!error}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={error ? onRetry : fetchNextPage}
        autoLoad={!error}
        label={error ? "Try again" : "Load more"}
      />
    </>
  );
}
